import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useLang, t, tx, SUPPORTED_LANGUAGES } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import ScrollProgress from './motion/ScrollProgress';
import { DUR, EASE } from '../lib/motion';

const LOGO_URL = '/logo-meteris.png';

const solutionLinks = [
  { slug: 'infogerance-cyber', label: { fr: 'Infogérance & Cyber', en: 'Managed IT & Cyber', es: 'Gestión IT y Ciberseguridad', de: 'IT-Betreuung & Cybersicherheit', it: 'Gestione IT e Cybersecurity', pt: 'Gestão IT e Cibersegurança' } },
  { slug: 'telephonie', label: { fr: 'Téléphonie', en: 'Telephony', es: 'Telefonía', de: 'Telefonie', it: 'Telefonia', pt: 'Telefonia' } },
  { slug: 'copieurs', label: { fr: 'Copieurs & Impression', en: 'Copiers & Printing', es: 'Copiadoras e Impresión', de: 'Kopierer & Druck', it: 'Fotocopiatrici e Stampa', pt: 'Copiadoras e Impressão' }, link: 'https://mps.meteris.fr', external: true },
  { slug: 'serveurs-infrastructure', label: { fr: 'Serveurs & Infrastructure', en: 'Servers & Infrastructure', es: 'Servidores e Infraestructura', de: 'Server & Infrastruktur', it: 'Server e Infrastruttura', pt: 'Servidores e Infraestrutura' } },
  { slug: 'ia-automatisation', label: { fr: 'IA & Automatisation', en: 'AI & Automation', es: 'IA y Automatización', de: 'KI & Automatisierung', it: 'IA e Automazione', pt: 'IA e Automação' } },
  { slug: 'formation', label: { fr: 'Formation', en: 'Training', es: 'Formación', de: 'Schulung', it: 'Formazione', pt: 'Formação' } },
];

// Menus déroulants : le panneau s'ouvre depuis son coin haut-gauche, ses entrées suivent en cascade.
const panel = {
  hidden: { opacity: 0, y: -6, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: DUR.fast, ease: EASE, staggerChildren: 0.03, delayChildren: 0.04 } },
  exit: { opacity: 0, y: -4, scale: 0.98, transition: { duration: 0.12, ease: 'easeIn' } },
};
const entry = {
  hidden: { opacity: 0, x: -6 },
  show: { opacity: 1, x: 0, transition: { duration: 0.22, ease: EASE } },
};

// Trait bleu sous l'entrée active : partagé par `layoutId`, il glisse d'une entrée à l'autre.
function ActiveMark() {
  return (
    <motion.span
      layoutId="nav-active"
      aria-hidden="true"
      className="absolute left-4 right-4 bottom-0.5 h-[2px] rounded-full bg-[#0099D6]"
      transition={{ type: 'spring', stiffness: 420, damping: 36 }}
    />
  );
}

export default function Navbar() {
  const { lang, setLang } = useLang();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSolOpen(false);
    setLangOpen(false);
  }, [location]);

  // Fermeture du dropdown langue sur clic extérieur
  useEffect(() => {
    if (!langOpen) return;
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [langOpen]);

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];
  const pickLang = (code) => {
    setLang(code);
    setLangOpen(false);
  };

  const nav = translations.nav;
  const isActive = (path) => location.pathname === path;
  const isSolutionActive = location.pathname.startsWith('/solutions');
  const getSolutionHref = (sol) => sol.link || `/solutions/${sol.slug}`;

  return (
    <nav
      data-testid="main-navbar"
      data-scrolled={scrolled ? 'true' : 'false'}
      className={`fixed top-[var(--persona-h,72px)] left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0B0C10]/95 backdrop-blur-xl border-b border-white/5 shadow-lg' : 'bg-transparent'
      }`}
    >
      {/* La barre se resserre dès que la page défile (80 → 64 px), le logo suit. */}
      <div
        className={`max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between transition-[height] duration-300 ease-out ${
          scrolled ? 'h-16' : 'h-20'
        }`}
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="MÉTÉRIS"
            className={`object-contain transition-all duration-300 ease-out ${scrolled ? 'h-8 w-8' : 'h-10 w-10'}`}
          />
          <span className="text-xl font-bold tracking-tight text-white group-hover:text-[#0099D6] transition-colors">
            MÉTÉRIS
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          <NavLink to="/" active={isActive('/')}>{t(nav.home, lang)}</NavLink>

          {/* Solutions Dropdown — padding-top bridge to prevent hover gap */}
          <div className="relative" onMouseEnter={() => setSolOpen(true)} onMouseLeave={() => setSolOpen(false)}>
            <button
              data-testid="solutions-dropdown-trigger"
              aria-haspopup="menu"
              aria-expanded={solOpen}
              onClick={() => setSolOpen((v) => !v)}
              className={`relative flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isSolutionActive ? 'text-[#0099D6]' : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              {t(nav.solutions, lang)}
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${solOpen ? 'rotate-180' : ''}`} />
              {isSolutionActive && <ActiveMark />}
            </button>
            <AnimatePresence>
              {solOpen && (
                <div key="solutions-menu" className="absolute top-full left-0 w-64 pt-2 z-50">
                  <motion.div
                    variants={panel}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    style={{ transformOrigin: 'top left' }}
                    className="bg-[#15171E]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl py-2"
                  >
                    {solutionLinks.map((sol) => (
                      <motion.div key={sol.slug} variants={entry}>
                        {sol.external ? (
                          <a
                            href={getSolutionHref(sol)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`nav-solution-${sol.slug}`}
                            className="block px-4 py-2.5 text-sm transition-all hover:bg-white/5 hover:text-[#0099D6] hover:pl-5 text-slate-300"
                          >
                            {t(sol.label, lang)}
                          </a>
                        ) : (
                          <Link
                            to={getSolutionHref(sol)}
                            data-testid={`nav-solution-${sol.slug}`}
                            className={`block px-4 py-2.5 text-sm transition-all hover:bg-white/5 hover:text-[#0099D6] hover:pl-5 ${
                              location.pathname === getSolutionHref(sol) ? 'text-[#0099D6] bg-white/5' : 'text-slate-300'
                            }`}
                          >
                            {t(sol.label, lang)}
                          </Link>
                        )}
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </div>

          <NavLink to="/entreprise" active={isActive('/entreprise')}>{t(nav.company, lang)}</NavLink>
          <NavLink to="/actualites" active={isActive('/actualites')}>{t(nav.news, lang)}</NavLink>
          <NavLink to="/prendre-rdv" active={isActive('/prendre-rdv')}>{tx(lang, 'Prendre RDV', 'Book', { es: 'Reservar cita', de: 'Termin buchen', it: 'Prenota', pt: 'Marcar' })}</NavLink>
          <NavLink to="/contact" active={isActive('/contact')}>{t(nav.contact, lang)}</NavLink>
        </div>

        {/* Right side: Lang dropdown + CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              data-testid="language-switcher"
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-sm text-slate-300 hover:text-white hover:border-white/20 transition-all"
            >
              <Globe className="w-3.5 h-3.5" />
              <span className="text-base leading-none" aria-hidden>{currentLang.flag}</span>
              <span>{currentLang.short}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.ul
                  key="lang-menu"
                  role="listbox"
                  variants={panel}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  style={{ transformOrigin: 'top right' }}
                  className="absolute right-0 top-full mt-2 w-44 bg-[#15171E]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl py-1 z-50"
                >
                  {SUPPORTED_LANGUAGES.map((opt) => (
                    <motion.li key={opt.code} variants={entry}>
                      <button
                        type="button"
                        onClick={() => pickLang(opt.code)}
                        data-testid={`lang-option-${opt.code}`}
                        aria-selected={opt.code === lang}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-all hover:bg-white/5 ${
                          opt.code === lang ? 'text-[#0099D6] bg-white/5' : 'text-slate-300 hover:text-white'
                        }`}
                      >
                        <span className="text-base leading-none" aria-hidden>{opt.flag}</span>
                        <span className="flex-1 text-left">{opt.label}</span>
                        <span className="text-xs text-slate-500">{opt.short}</span>
                      </button>
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="btn-shine bg-[#0099D6] hover:bg-[#0284C7] text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-[0_0_20px_rgba(0,153,214,0.3)] hover:shadow-[0_0_30px_rgba(0,153,214,0.4)] transition-all hover:scale-105 active:scale-95"
          >
            {t(translations.common.audit_cta, lang)}
          </Link>
        </div>

        {/* Mobile toggle : l'icône seule n'a pas de nom ; lecteurs d'écran et assistants lisent aria-label. */}
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={mobileOpen
            ? tx(lang, 'Fermer le menu', 'Close menu', { es: 'Cerrar el menú', de: 'Menü schließen', it: 'Chiudi il menu', pt: 'Fechar o menu' })
            : tx(lang, 'Ouvrir le menu', 'Open menu', { es: 'Abrir el menú', de: 'Menü öffnen', it: 'Apri il menu', pt: 'Abrir o menu' })}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          className="lg:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile Menu : le volet se déplie en hauteur, ses entrées suivent en cascade. */}
      <AnimatePresence initial={false}>
        {mobileOpen && (
          <motion.div
            key="mobile-menu"
            id="mobile-menu"
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1, transition: { duration: 0.3, ease: EASE } }}
            exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } }}
            className="lg:hidden overflow-hidden bg-[#0B0C10]/98 backdrop-blur-xl border-t border-white/5"
          >
            <motion.div
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.035, delayChildren: 0.05 } } }}
              initial="hidden"
              animate="show"
              className="px-6 py-6 space-y-1"
            >
              <MobileLink to="/">{t(nav.home, lang)}</MobileLink>
              <motion.div variants={entry} className="py-1">
                <p className="text-xs uppercase tracking-widest text-slate-500 px-4 py-2">{t(nav.solutions, lang)}</p>
                {solutionLinks.map((sol) => (
                  sol.external ? (
                    <MobileExternalLink key={sol.slug} href={getSolutionHref(sol)} indent>
                      {t(sol.label, lang)}
                    </MobileExternalLink>
                  ) : (
                    <MobileLink key={sol.slug} to={getSolutionHref(sol)} indent>
                      {t(sol.label, lang)}
                    </MobileLink>
                  )
                ))}
              </motion.div>
              <MobileLink to="/entreprise">{t(nav.company, lang)}</MobileLink>
              <MobileLink to="/actualites">{t(nav.news, lang)}</MobileLink>
              <MobileLink to="/prendre-rdv">{tx(lang, 'Prendre RDV', 'Book', { es: 'Reservar cita', de: 'Termin buchen', it: 'Prenota', pt: 'Marcar' })}</MobileLink>
              <MobileLink to="/contact">{t(nav.contact, lang)}</MobileLink>
              <motion.div variants={entry} className="pt-4 border-t border-white/5 mt-4">
                <p className="text-xs uppercase tracking-widest text-slate-500 px-4 mb-2 flex items-center gap-1.5">
                  <Globe className="w-3 h-3" />
                  {tx(lang, 'Langue', 'Language', { es: 'Idioma', de: 'Sprache', it: 'Lingua', pt: 'Idioma' })}
                </p>
                <div className="grid grid-cols-3 gap-1.5 mb-4">
                  {SUPPORTED_LANGUAGES.map((opt) => (
                    <button
                      key={opt.code}
                      type="button"
                      onClick={() => pickLang(opt.code)}
                      data-testid={`mobile-lang-option-${opt.code}`}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition-all ${
                        opt.code === lang
                          ? 'bg-[#0099D6] text-white'
                          : 'bg-white/5 text-slate-300 hover:bg-white/10'
                      }`}
                    >
                      <span aria-hidden>{opt.flag}</span>
                      <span>{opt.short}</span>
                    </button>
                  ))}
                </div>
                <Link to="/contact" className="block bg-[#0099D6] text-white text-sm font-semibold px-4 py-2.5 rounded-lg text-center">
                  {t(translations.common.audit_cta, lang)}
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ScrollProgress visible={scrolled} />
    </nav>
  );
}

function NavLink({ to, active, children }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active ? 'text-[#0099D6]' : 'text-slate-300 hover:text-white hover:bg-white/5'
      }`}
    >
      {children}
      {active && <ActiveMark />}
    </Link>
  );
}

function MobileLink({ to, children, indent }) {
  return (
    <motion.div variants={entry}>
      <Link
        to={to}
        className={`block px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-all ${indent ? 'pl-8' : ''}`}
      >
        {children}
      </Link>
    </motion.div>
  );
}

function MobileExternalLink({ href, children, indent }) {
  return (
    <motion.div variants={entry}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`block px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-all ${indent ? 'pl-8' : ''}`}
      >
        {children}
      </a>
    </motion.div>
  );
}
